import React, { useRef } from "react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";
import { Box } from "@mui/material";
import master from "../assets/master.png";
import { useHistory, useLocation } from "react-router-dom";

const HeroCarousel = ({ data = [], fallbackImage, className = "" }) => {
  const location = useLocation();
  const history = useHistory();
  const isProductDetails = location.pathname.includes("product-details");

  // slidesRef always holds the current slides array so handleSlideClick
  // can read from it without stale closure issues.
  const slidesRef = useRef([]);

  // Build slides directly from the raw API data — no useState, no useEffect,
  // no transformation that could lose or reassign the original business id.
  // Each slide object carries the exact id that came from the database.
  const slides = data
    .map((item) => ({
      businessId: item?.id,                                          // original DB primary key
      image: isProductDetails ? item?.cover_image : item?.banner_image,
    }))
    .filter((s) => Boolean(s.image));

  // Keep ref in sync with current render so the click handler always
  // reads the correct slides array even after re-renders.
  slidesRef.current = slides;

  // react-responsive-carousel calls onClickItem(index, item) where:
  //   index — the rendered position (may be offset by ±1 with infiniteLoop
  //            because the library clones boundary slides in the DOM)
  //   item  — the React element of the clicked slide (NOT a DOM node)
  //
  // Strategy: read businessId from the img element's data-business-id
  // attribute via the event target, which is always the exact DOM element
  // the user physically tapped/clicked — immune to index offset.
  const handleSlideClick = (index, item) => {
    if (isProductDetails) return;

    // Primary: read id from the DOM element the user actually clicked.
    // item in react-responsive-carousel is the React element; we need the
    // real DOM node. We get it from the event that is still on the stack.
    const clickedImg = document.activeElement?.closest("[data-business-id]")
      || document.querySelector(".carousel .slide.selected [data-business-id]");

    const idFromDOM = clickedImg?.getAttribute("data-business-id");

    if (idFromDOM) {
      history.push(`/product-details/${idFromDOM}`);
      return;
    }

    // Fallback: use the index against our ref-backed slides array.
    // With infiniteLoop the library reports the logical index (0-based,
    // matching our slides array) not the DOM clone index, so this is safe.
    const slide = slidesRef.current[index];
    if (slide?.businessId) {
      history.push(`/product-details/${slide.businessId}`);
    }
  };

  if (slides.length === 0) {
    return (
      <Box>
        <Carousel
          showIndicators={false}
          showThumbs={false}
          showStatus={false}
          autoPlay={false}
          infiniteLoop={false}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: { xs: "186px", sm: "260px", md: "487px", lg: "526px" },
              position: "relative",
              overflow: "hidden",
            }}
          >
            <img
              src={fallbackImage || master}
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }}
              loading="lazy"
              alt="hero"
            />
            <Box
              className="slideshow-inner-shadow"
              sx={{ display: "block" }}
            />
          </Box>
        </Carousel>
      </Box>
    );
  }

  return (
    <Box className={className}>
      <Carousel
        showIndicators={true}
        showThumbs={false}
        showStatus={false}
        autoPlay={slides.length > 1}
        infiniteLoop={true}
        animationHandler="fade"
        onClickItem={handleSlideClick}
        style={{ cursor: isProductDetails ? "default" : "pointer" }}
      >
        {slides.map((slide) => (
          <Box
            key={slide.businessId}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: isProductDetails 
                ? { xs: "482.6px", sm: "562.4px", md: "668.6px", lg: "757.5px" }
                : { xs: "297px", sm: "260px", md: "487px", lg: "526px" },
              cursor: isProductDetails ? "default" : "pointer",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <img
              data-business-id={slide.businessId}
              src={slide.image}
              style={{
                width: isProductDetails ? "95%" : "100%",
                height: isProductDetails ? "95%" : "100%",
                objectFit: isProductDetails ? "contain" : "cover",
                objectPosition: "center center",
                display: "block",
                margin: isProductDetails ? "0 auto" : "0 auto",
              }}
              loading="lazy"
              alt="business"
            />
            <Box
              className="slideshow-inner-shadow"
              sx={{ display: "block" }}
            />
          </Box>
        ))}
      </Carousel>
    </Box>
  );
};

export default React.memo(HeroCarousel);
